import { useEffect, useRef, useState } from "react";
import { Heart, ChevronDown, ChevronUp, Users, Star, Quote, Sparkles } from "lucide-react";

const HEALING_HOPE_URL = "https://customer-assets.emergentagent.com/job_tyss-redesigned/artifacts/athoek9v_rs%3Dw_1200%2Ch_600%2Ccg_true.webp";
const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_bba26203-6076-4f12-90a8-dadbcd0e3679/artifacts/saif0ura_unnamed.jpg";
const NICK_IMAGE = "https://customer-assets.emergentagent.com/job_tyss-redesigned/artifacts/j4idxi4q_rs%3Dw_730%2Ch_730%2Ccg_true.webp";
const MARK_IMAGE = "https://customer-assets.emergentagent.com/job_tyss-redesigned/artifacts/na52915t_rs%3Dw_365%2Ch_365%2Ccg_true.webp";
const JR_IMAGE = "https://customer-assets.emergentagent.com/job_trauma-youth-digital/artifacts/95rcs6rd_IMG_3687.JPG";

const SUPPORT_HEROES = [
  { image: NICK_IMAGE, name: "Nick - TYSS Advisor", shortBio: "Nicholas Krasucki, MBA, CQE (Dartmouth College '01, Boston University '13) serves on the Board of Advisors for Trauma Youth Support Services, Inc. With over 20 years of experience as a biomedical quality engineer and corporate fiduciary, Nick has held leadership roles at Infraredx-Nipro, Medtronic, Sober Grid, and Thrive Digital Health.", fullBio: "As Co-Founder and CFO of Sober Grid, he raised over $14M in funding for social impact startups in addiction recovery technology. Now developing next-generation medical devices for vascular care, he supports TYSS's mission to close gaps in mental health resources for caregivers and families." },
  { image: MARK_IMAGE, name: "Mark - TYSS Consultant", shortBio: "Mark D. Reeves is the Founder and CEO of New England Community Services (NECS), a non-profit organization established in 2007. NECS aims to provide counseling, advocacy, modeling, monitoring, educational, recreational, and other supports to inner-city youth in order to afford them the opportunity to have the best tools to become successful.", fullBio: "Under Mark's leadership, NECS has grown into a cornerstone of community support, fostering resilience and opportunity for underserved youth across New England. His dedication to empowering young people aligns with TYSS's mission to ensure no family faces crisis alone." },
  { image: JR_IMAGE, name: "J.R. - Peer Support Specialist", imagePosition: "center 25%", shortBio: "J.R. is the catalyst for Trauma Youth Support Services (TYSS). Drawing on his own experiences navigating mental health challenges, he is passionate about supporting peers who face similar obstacles. In his free time, J.R. enjoys playing soccer, basketball, and making music.", fullBio: "" },
];

const BOARD_MEMBERS = [
  { name: "Cynthia Reeves, M.Ed", role: "TYSS Executive Director", position: "President" },
  { name: "Paula Koonce - Merrill Lynch", role: "", position: "Treasurer" },
  { name: "Steffi Wright, Early Childhood Educator", role: "", position: "Secretary" },
  { name: "Michael Swinton - Sr. Director, AYURS", role: "", position: "Board Member" },
  { name: "Dr. Darrin Slade, Superintendent of Kalamazoo Public Schools", role: "", position: "Board Member" },
];

function useScrollReveal(ref) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible"));
        }
      }),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

function HeroCard({ person, index }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid={`hero-card-${index}`} className={`animate-on-scroll delay-${Math.min((index+1)*100,300)} rounded-2xl bg-white border border-[#C8DDD8]/50 overflow-hidden card-lift group`}>
      <div className="relative h-72 overflow-hidden">
        <img src={person.image} alt={person.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ objectPosition: person.imagePosition || "center top" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A4A]/70 via-[#1A3A4A]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-[#7CC8E0]" />
            <span className="text-xs font-sans tracking-wider uppercase text-[#7CC8E0] font-semibold">{person.name.split(' - ')[1]}</span>
          </div>
          <h3 className="text-xl font-serif font-bold text-white">{person.name.split(' - ')[0]}</h3>
        </div>
      </div>
      <div className="p-6">
        <p className="text-sm font-sans text-earth-muted leading-relaxed">{person.shortBio}</p>
        {person.fullBio && (
          <>
            <div className={`overflow-hidden transition-all duration-500 ${expanded ? "max-h-96 opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
              <p className="text-sm font-sans text-earth-muted leading-relaxed">{person.fullBio}</p>
            </div>
            <button onClick={() => setExpanded(!expanded)} data-testid={`hero-expand-${index}`}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-sans font-semibold text-[#0E8DB5] hover:text-[#0A7599] transition-all duration-300 bg-transparent border-none cursor-pointer group/btn">
              {expanded ? "Show Less" : "Show More"}
              <span className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}>
                <ChevronDown className="w-4 h-4" />
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AboutPage() {
  const s1 = useRef(null); const s2 = useRef(null); const s3 = useRef(null);
  const s4 = useRef(null); const s5 = useRef(null); const s6 = useRef(null);
  useScrollReveal(s1); useScrollReveal(s2); useScrollReveal(s3);
  useScrollReveal(s4); useScrollReveal(s5); useScrollReveal(s6);

  return (
    <main className="pt-24">
      {/* Healing & Hope Header - FULL IMAGE */}
      <section className="relative bg-[#2D5A3D]">
        <img
          src={HEALING_HOPE_URL}
          alt="Healing and Hope - TYSS"
          data-testid="about-healing-hope-image"
          className="w-full h-auto block mx-auto"
          style={{ maxHeight: "400px", objectFit: "contain", backgroundColor: "#2D5A3D" }}
        />
      </section>

      {/* Decorative divider */}
      <div className="h-1 bg-gradient-to-r from-[#0E8DB5] via-[#3DAA35] to-[#0E8DB5]" />

      {/* Our Mission */}
      <section ref={s1} className="py-24 bg-[#F8FAFB] relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#0E8DB5]/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#3DAA35]/5 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <div className="scale-in text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="line-animate h-[2px] w-10 bg-[#0E8DB5]" />
              <span className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#0E8DB5]">Our Mission</span>
              <div className="line-animate h-[2px] w-10 bg-[#0E8DB5]" />
            </div>
            <h2 data-testid="about-mission-heading" className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-earth text-glow">
              Our <span className="gradient-text">Mission</span>
            </h2>
          </div>

          <div className="space-y-6">
            <p className="slide-left text-base sm:text-lg font-sans font-medium leading-relaxed text-earth-muted">
              Trauma Youth Support Services, Inc. (TYSS) is a nonprofit organization dedicated to supporting and empowering caregivers of children with mental illness — especially those navigating crisis, hospitalization, and complex systems of care.
            </p>
            <p className="slide-right delay-100 text-base sm:text-lg font-sans font-medium leading-relaxed text-earth-muted">
              Parenting a child with mental illness can feel overwhelming — but caregivers do not have to face it alone. TYSS stands alongside families with compassion, credibility, and community.
            </p>
            <p className="slide-left delay-200 text-base sm:text-lg font-sans font-medium leading-relaxed text-earth-muted">
              Our mission is to ensure caregivers have the compassion, tools, and support they need to help their families heal, grow, and thrive.
            </p>
          </div>

          <div className="scale-in delay-300 mt-10 text-center">
            <a href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer" data-testid="about-donate-btn"
              className="btn-pill btn-primary text-base px-10 py-4 no-underline inline-flex shadow-lg shadow-[#0E8DB5]/30 pulse-glow">
              <Heart className="w-5 h-5" /> Donate
            </a>
          </div>
        </div>
      </section>

      {/* Section divider */}
      <div className="section-divider max-w-4xl mx-auto" />

      {/* Founder's Journey */}
      <section ref={s2} className="py-24 bg-bone relative overflow-hidden">
        <div className="absolute top-10 right-10 w-32 h-32 border border-[#0E8DB5]/10 rounded-full" />
        <div className="absolute bottom-10 left-10 w-24 h-24 border border-[#3DAA35]/10 rounded-full" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="scale-in text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="line-animate h-[2px] w-10 bg-[#3DAA35]" />
              <span className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#0E8DB5]">Our Story</span>
              <div className="line-animate h-[2px] w-10 bg-[#3DAA35]" />
            </div>
            <h2 data-testid="about-founder-heading" className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-earth">
              Founder's <span className="gradient-text">Journey</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-12 items-start">
            <div className="md:col-span-2 slide-left">
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-br from-[#0E8DB5]/10 to-[#3DAA35]/10 rounded-3xl" />
                <img src={FOUNDER_IMAGE} alt="TYSS Founder" className="relative w-full h-[420px] object-cover object-top rounded-2xl shadow-xl" />
              </div>
              <div className="mt-4 flex items-center gap-3 p-4 rounded-xl bg-white border border-[#C8DDD8]/50">
                <Quote className="w-5 h-5 text-[#0E8DB5] flex-shrink-0" />
                <p className="text-xs font-sans italic text-earth-muted">M.Ed. | 20+ Years in Mental Health Advocacy</p>
              </div>
            </div>

            <div className="md:col-span-3 space-y-5">
              <p className="slide-right text-base sm:text-lg font-sans leading-relaxed text-earth-muted">
                I have spent more than 20 years working in mission-driven nonprofit organizations and advocating for mental health support for children and families. I hold a Master of Education (M.Ed.) and am the parent of two children adopted from foster care who live with complex mental health challenges.
              </p>
              <p className="slide-right delay-100 text-base sm:text-lg font-sans leading-relaxed text-earth-muted">
                I founded Trauma Youth Support Services (TYSS) as a caregiver and a labor of love after personally navigating the fear, exhaustion, and isolation that often accompany raising a child in mental health crisis.
              </p>
              <p className="slide-right delay-200 text-base sm:text-lg font-sans leading-relaxed text-earth-muted">
                That lived experience revealed a critical gap — while children receive clinical care, caregivers are frequently left to manage advocacy, emotional strain, isolation, and burnout alone.
              </p>
              <div className="slide-right delay-300 p-5 rounded-xl bg-gradient-to-r from-[#0E8DB5]/5 to-[#3DAA35]/5 border-l-4 border-[#0E8DB5]">
                <p className="text-lg font-serif font-bold text-earth italic">
                  "TYSS exists to change that."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Support Heroes */}
      <section ref={s3} className="py-24 bg-[#F8FAFB] relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#0E8DB5]/3 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="scale-in text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="line-animate h-[2px] w-10 bg-[#0E8DB5]" />
              <Sparkles className="w-4 h-4 text-[#0E8DB5] icon-float" />
              <span className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#0E8DB5]">Leadership</span>
              <Sparkles className="w-4 h-4 text-[#0E8DB5] icon-float" />
              <div className="line-animate h-[2px] w-10 bg-[#0E8DB5]" />
            </div>
            <h2 data-testid="about-heroes-heading" className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-earth">
              Our Support <span className="gradient-text">Heroes</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {SUPPORT_HEROES.map((person, i) => <HeroCard key={person.name} person={person} index={i} />)}
          </div>
        </div>
      </section>

      {/* Meet Our Support Team */}
      <section ref={s4} className="py-24 bg-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="scale-in text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="line-animate h-[2px] w-10 bg-[#3DAA35]" />
              <span className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#0E8DB5]">Board</span>
              <div className="line-animate h-[2px] w-10 bg-[#3DAA35]" />
            </div>
            <h2 data-testid="about-team-heading" className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-earth">
              Meet Our <span className="gradient-text">Support Team</span>
            </h2>
          </div>

          <div className="space-y-4">
            {BOARD_MEMBERS.map((member, i) => (
              <div key={member.name} data-testid={`board-member-${i}`}
                className={`stagger-item flex items-center gap-5 p-5 rounded-2xl bg-white border border-[#C8DDD8]/50 card-lift group`}
                style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0E8DB5]/10 to-[#3DAA35]/10 flex items-center justify-center flex-shrink-0 group-hover:from-[#0E8DB5]/20 group-hover:to-[#3DAA35]/20 transition-all duration-300">
                  <Users className="w-5 h-5 text-[#0E8DB5]" />
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-serif font-bold text-earth group-hover:text-[#0E8DB5] transition-colors duration-300">{member.name}</h4>
                  <p className="text-xs font-sans tracking-wider uppercase text-[#0E8DB5] font-semibold">{member.position}</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-[#3DAA35] scale-breathe" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={s5} className="py-20 bg-[#1A3A4A] text-white text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#0E8DB5]/10 rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#3DAA35]/10 rounded-full -translate-x-1/2 translate-y-1/2 blur-3xl" />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="scale-in">
            <div className="w-14 h-14 rounded-full bg-[#0E8DB5]/20 flex items-center justify-center mx-auto mb-6 icon-float">
              <Heart className="w-7 h-7 text-[#7CC8E0]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4">Join Our <span className="text-[#7CC8E0]">Mission</span></h2>
          </div>
          <p className="animate-on-scroll delay-100 text-base font-sans text-white/70 mb-8">Together, we can ensure no family faces crisis alone.</p>
          <a href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer"
            className="animate-on-scroll delay-200 btn-pill btn-primary text-base px-10 py-4 no-underline inline-flex shadow-lg shadow-[#0E8DB5]/30 pulse-glow">
            <Heart className="w-5 h-5" /> Donate Now
          </a>
        </div>
      </section>
    </main>
  );
}
